import React, { createContext, useContext, useState, useEffect } from 'react';

// Define theme interface
interface Theme {
    system: string | null;
    color: string | null;
    scale: string | null;
    followSystem: boolean;
    screen: 'S' | 'M' | 'L' | 'XL'; // Updated to match Spectrum's breakpoint naming
}

// Define context type
interface ThemeContextType {
    theme: Theme;
    updateTheme: (updates: Partial<Theme>) => void;
}

// Create a context for theme values with proper typing
const ThemeContext = createContext<ThemeContextType | null>(null);

// Local storage key
const LOCAL_STORAGE_KEY = 'talisman-theme-values';

// Screen size breakpoints in pixels following React Spectrum's mobile-first approach
// Each breakpoint represents "this size and above"
const SCREEN_BREAKPOINTS = {
    S: 0,         // Small: 0px and above
    M: 768,       // Medium: 768px and above
    L: 1024,      // Large: 1024px and above
    XL: 1280,     // Extra large: 1280px and above
};

// Function to determine current screen size using mobile-first approach
const getScreenSize = (): 'S' | 'M' | 'L' | 'XL' => {
    const width = window.innerWidth;

    // Mobile-first: return the largest breakpoint that applies
    if (width >= SCREEN_BREAKPOINTS.XL) return 'XL';
    if (width >= SCREEN_BREAKPOINTS.L) return 'L';
    if (width >= SCREEN_BREAKPOINTS.M) return 'M';
    return 'S';
};

// Default theme shape
const defaultTheme: Theme = {
    system: null,
    color: null,
    scale: null,
    followSystem: true, // Default to following system
    screen: getScreenSize() // Default to base screen size (mobile-first)
};

// Custom hook that provides theme functionality
export const useTheme = (): [Theme, (updates: Partial<Theme>) => void] => {
    const context = useContext(ThemeContext);
    if (context === null) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    // Return an array like other hooks (useState, useReducer, etc.)
    return [context.theme, context.updateTheme];
};

// Theme Provider component props type
interface ThemeProviderProps {
    children: React.ReactNode;
}

// Theme Provider component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    // Get initial state from localStorage if available
    const getInitialTheme = (): Theme => {
        try {
            const storedTheme = localStorage.getItem(LOCAL_STORAGE_KEY);
            const parsedTheme: Theme = storedTheme
                ? JSON.parse(storedTheme)
                : { ...defaultTheme };

            // If followSystem is not defined, set default to true
            if (parsedTheme.followSystem === undefined) {
                parsedTheme.followSystem = true;
            }

            // If no color preference is saved or followSystem is true, detect browser's dark mode setting
            if (parsedTheme.color === null || parsedTheme.followSystem) {
                const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
                parsedTheme.color = prefersDarkMode ? 'dark' : 'light';
            }

            // Set initial screen size based on current viewport
            parsedTheme.screen = getScreenSize();

            return parsedTheme;
        } catch (error) {
            console.error('Failed to parse theme values from localStorage:', error);
            // Default with browser dark mode detection and screen size
            const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
            return {
                ...defaultTheme,
                color: prefersDarkMode ? 'dark' : 'light',
                screen: getScreenSize()
            };
        }
    };

    // Initialize state from localStorage
    const [theme, setTheme] = useState<Theme>(getInitialTheme);

    // Listen for localStorage changes
    useEffect(() => {
        const handler = (e: StorageEvent) => {
            if (e.key !== LOCAL_STORAGE_KEY) return;
            updateTheme(getInitialTheme())
        }
        window.addEventListener('storage', handler)
        return () => {
            window.removeEventListener('storage', handler)
        }
    }, [theme]);

    // Function to update theme values
    const updateTheme = (updates: Partial<Theme>): void => {
        setTheme(current => {
            const updatedTheme = { ...current, ...updates };

            // Turn off system following only for explicit user color changes
            // We detect this by checking if color is being updated without followSystem
            // This should not affect system-initiated changes which include both
            if ('color' in updates && !('followSystem' in updates)) {
                // This is likely a user-initiated color change
                updatedTheme.followSystem = false;
            }

            // Save to localStorage
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedTheme));

            // Dispatch theme change event
            const event = new CustomEvent('talisman:theme-change', {
                bubbles: true,
                detail: updatedTheme
            });
            document.dispatchEvent(event);

            return updatedTheme;
        });
    };

    // Listen for system color scheme changes
    useEffect(() => {
        const colorSchemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        // Handler for system color scheme changes
        const handleColorSchemeChange = (e: MediaQueryListEvent): void => {
            // Apply if theme is set to follow system preference
            if (theme.followSystem) {
                // When updating from system change, include followSystem in the update
                // to prevent it from being automatically set to false
                updateTheme({
                    color: e.matches ? 'dark' : 'light',
                    followSystem: true // Keep followSystem true for system-initiated changes
                });
            }
        };

        // Add event listener for system color scheme changes
        colorSchemeMediaQuery.addEventListener('change', handleColorSchemeChange);

        // Clean up when component unmounts
        return () => {
            colorSchemeMediaQuery.removeEventListener('change', handleColorSchemeChange);
        };
    }, [theme.followSystem]); // Only re-run if followSystem changes

    // Listen for screen size changes
    useEffect(() => {
        // Handler for window resize events
        const handleResize = (): void => {
            const newScreenSize = getScreenSize();
            if (newScreenSize !== theme.screen) {
                updateTheme({ screen: newScreenSize });
            }
        };

        // Add event listener for window resize
        window.addEventListener('resize', handleResize);

        // Clean up when component unmounts
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [theme.screen]); // Only re-run if screen size changes

    // Create context value with theme state and update function
    const contextValue: ThemeContextType = {
        theme,
        updateTheme
    };

    return (
        <ThemeContext.Provider value={contextValue}>
            {children}
        </ThemeContext.Provider>
    );
};